// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

// Importing react and style
import './style.scss';
import React from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';

// Importing Custom Pages
import Login from './pages/Login';
import LicenseAgreements from './shared_components/LicenseAgreements/LicenseAgreements';
import Deliveries from './pages/Deliveries';
import Delivery from './pages/Delivery';
import LoadingScreen from './pages/LoadingScreen';
import Labeling from './pages/Labeling';
import AccessDenied from './pages/AccessDenied';

// Importing context provider.
import { auth, firestore } from './context/firebase';
import {
  ACCESS_DENIED_ROUTE,
  DELIVERIES_ROUTE,
  DELIVERY_ROUTE,
  LABELING_ROUTE,
  LICENSE_AGREEMENT_ROUTE,
  LOADING_ROUTE,
  LOGIN_ROUTE,
} from './constant/routes';
import { useAuthStore } from './stores/authStore';
import { useCameraStore } from './stores/cameraStore';

function App() {
  // Creating the user
  const authInit = useAuthStore((state) => state.authInit);
  const authUser = useAuthStore((state) => state.authUser);
  const currentUserUid = useAuthStore((state) => state.currentUser?.uid);
  const currentUserLicenseAgreements = useAuthStore((state) => state.currentUser?.info?.legal?.license_agreement);
  const userLoaded = useAuthStore((state) => state.userLoaded);
  const requiredLicenseAgreements = useAuthStore((state) => state.requiredLicenseAgreements);
  const deliveriesAppEnabled = useAuthStore((state) => state.deliverySettings?.features?.enable_deliveries_app);

  const cameraInit = useCameraStore((state) => state.cameraInit);

  function getUnsignedLicenseAgreement() {
    /* Check if the user has agreed to all required license agreements */
    for (const agreement of requiredLicenseAgreements) {
      if (!currentUserLicenseAgreements?.[agreement]?.accepted) {
        return agreement;
      }
    }
    return null;
  }

  // Creating a protected route to only allow logged-in users to access the dashboard.
  const ProtectedRoute = ({ children }) => {
    if (!authUser) {
      return <Navigate to={LOGIN_ROUTE} />;
    }

    // Check if the deliveries app is enabled
    if ((deliveriesAppEnabled || false) === false) {
      return <Navigate to={ACCESS_DENIED_ROUTE} />;
    }

    // Check if the user has agreed to all required license agreements
    const unsignedLicenseAgreementDocId = getUnsignedLicenseAgreement();
    if (unsignedLicenseAgreementDocId) {
      return <Navigate to={LICENSE_AGREEMENT_ROUTE} state={{ licenseAgreementDocId: unsignedLicenseAgreementDocId }} />;
    }

    return children;
  };

  // Block pinch zoom on mobile devices
  React.useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    }
  }, []);

  // Disable body scrolling (prevent bounce) while this component is mounted
  React.useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    // touch-action none helps on some browsers to suppress scroll/zoom defaults
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  // Handle orientation changes to reset scroll position
  React.useEffect(() => {
    const handleOrientationChange = () => {
      // Reset window scroll position to top-left
      window.scrollTo(0, 0);
    };

    // Listen for orientation change events
    window.addEventListener('orientationchange', handleOrientationChange);

    // Also listen for resize events as a fallback for some devices
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  React.useEffect(() => {
    authInit();
    cameraInit();
  }, []);

  if (authUser && !userLoaded) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path='/'>
          <Route
            index
            element={
              <ProtectedRoute>
                <Deliveries />
              </ProtectedRoute>
            }
          />{' '}
          {/* Wrapping home in the protected route */}
          <Route path={LOGIN_ROUTE} element={<Login />} />
          <Route
            path={LICENSE_AGREEMENT_ROUTE}
            element={<LicenseAgreements firestore={firestore} auth={auth} uid={currentUserUid} />}
          />
          <Route path={ACCESS_DENIED_ROUTE} element={<AccessDenied type='DELIVERIES_APP_DISABLED' />} />
          <Route
            path={DELIVERIES_ROUTE}
            element={
              <ProtectedRoute>
                <Deliveries />
              </ProtectedRoute>
            }
          />
          <Route
            path={DELIVERY_ROUTE}
            element={
              <ProtectedRoute>
                <Delivery />
              </ProtectedRoute>
            }
          />
          <Route
            path={LABELING_ROUTE}
            element={
              <ProtectedRoute>
                <Labeling />
              </ProtectedRoute>
            }
          />
          <Route
            path={LOADING_ROUTE}
            element={
              <ProtectedRoute>
                <LoadingScreen />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
